/**
 * Hook to fetch and post approval events (activity log)
 */
import { useContext, useMemo } from "react";
import useSWR from "swr";
import axios from "axios";
import { endpoints } from "../../../../../endpoints";
import type { WithPagination } from "../../../../../types/types";
import type {
  ApprovalEvent,
  ApprovalMessagePayload,
} from "../../../../../types/types.Workflow";
import { useStoreState } from "../../../../../util/util";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { useTranslation } from "react-i18next";

interface UseApprovalEventsParams {
  approvalId: string | null;
  limit?: number;
  offset?: number;
}

export function useApprovalEvents({
  approvalId,
  limit = 50,
  offset = 0,
}: UseApprovalEventsParams) {
  const { storefront_id } = useStoreState();
  const { notifyError } = useContext(Notifications);
  const { t } = useTranslation();

  const params = useMemo(
    () => ({
      params: { limit, offset },
    }),
    [limit, offset]
  );

  const { data, error, mutate, isValidating } = useSWR<
    WithPagination<{ data: ApprovalEvent[] }>
  >(
    storefront_id && approvalId
      ? [
          endpoints.v2_storefronts_id_integrations_approvals_id_events(
            storefront_id,
            approvalId
          ),
          params,
        ]
      : null
  );

  const postComment = async (message: ApprovalMessagePayload) => {
    if (!storefront_id || !approvalId) return;

    try {
      await axios.post(
        endpoints.v2_storefronts_id_integrations_approvals_id_events(
          storefront_id,
          approvalId
        ),
        message
      );
      await mutate();
    } catch (err) {
      notifyError(t("Failed to add comment"), { error: err });
      throw err;
    }
  };

  return {
    events: data?.data ?? [],
    pagination: data?.pagination,
    error,
    isLoading: !data && !error && !!approvalId,
    isValidating,
    mutate,
    postComment,
  };
}
